import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseQueryOptions,
} from '@tanstack/react-query';

import { apiGet, apiPath, apiPost } from '@/lib/api/client';
import { CubixApiError } from '@/lib/api/types';

import type {
  ChangePasswordRequest,
  CreateUserRequest,
  MeResponse,
  SetStatusRequest,
  UserResponse,
} from './types';

const usersKey = ['users', 'list'] as const;
const meKey    = ['auth', 'me']    as const;

/** GET /api/v1/users — admin only (cx-alm enforces). */
export function useUsersList(
  options?: Omit<UseQueryOptions<UserResponse[], CubixApiError>, 'queryKey' | 'queryFn'>,
) {
  return useQuery({
    queryKey: usersKey,
    queryFn: () => apiGet<UserResponse[]>(apiPath('users')),
    ...options,
  });
}

/** POST /api/v1/users — admin creates a new user. */
export function useCreateUser() {
  const qc = useQueryClient();
  return useMutation<UserResponse, CubixApiError, CreateUserRequest>({
    mutationFn: (body) =>
      apiPost<UserResponse, CreateUserRequest>(apiPath('users'), body),
    onSuccess: () => qc.invalidateQueries({ queryKey: usersKey }),
  });
}

/** POST /api/v1/users/{userId}/password — admin resets another user's password. */
export function useAdminResetPassword(userId: string) {
  return useMutation<void, CubixApiError, { newPassword: string }>({
    mutationFn: ({ newPassword }) =>
      apiPost<void, ChangePasswordRequest>(
        apiPath('users', userId, 'password'),
        { newPassword },
      ),
  });
}

/** POST /api/v1/users/{userId}/status — admin locks / unlocks / disables. */
export function useSetUserStatus(userId: string) {
  const qc = useQueryClient();
  return useMutation<void, CubixApiError, SetStatusRequest>({
    mutationFn: (body) =>
      apiPost<void, SetStatusRequest>(apiPath('users', userId, 'status'), body),
    onSuccess: () => qc.invalidateQueries({ queryKey: usersKey }),
  });
}

/** GET /api/v1/auth/me — current identity (server-side projection of JWT). */
export function useMe(
  options?: Omit<UseQueryOptions<MeResponse, CubixApiError>, 'queryKey' | 'queryFn'>,
) {
  return useQuery({
    queryKey: meKey,
    queryFn: () => apiGet<MeResponse>(apiPath('auth', 'me')),
    ...options,
  });
}

/** POST /api/v1/auth/me/password — self-service password change. */
export function useChangeOwnPassword() {
  return useMutation<void, CubixApiError, ChangePasswordRequest>({
    mutationFn: (body) =>
      apiPost<void, ChangePasswordRequest>(apiPath('auth', 'me', 'password'), body),
  });
}
